import type { ProofOfFundsStatus } from '@1trading/fast-api-domain';
import { HTTPError } from 'ky';
import { bopApi } from '@/lib/api/client';
import { formatDate, formatFriendlyTimestamp } from '@/utils/format';
import { parseEpochNumber } from '@/utils/number';

export type UserType = 'BROKER' | 'BROKER_CLIENT' | 'BROKER_CLIENT_SUBACCOUNT' | 'ONE_TRADING_CLIENT';
export type UserSegment = 'RETAIL' | 'INSTITUTIONAL' | 'MARKET_MAKER';
export type UserState = 'ACTIVE' | 'BLOCKED' | 'PENDING' | 'OFFBOARDED' | 'DELETED';
export type StatusChangeAction = 'ACTIVE' | 'BLOCKED' | 'PENDING' | 'OFFBOARDED' | 'CLOSED' | 'REACTIVATED';

/**
 * 200 body of GET /v1/user/{id}. Merged from 6+ stores —
 * any field except pid/user_type/segment can be absent.
 */
export interface RawUser {
  /** canonical PID (uuid) — authoritative; MAY DIFFER from the requested id */
  pid: string;
  fast_id: string | null;
  user_type: UserType;
  /** epoch as string (ms in practice; reshape sniffs s/ms/µs/ns) */
  created?: string;
  /** never PENDING on the wire — that state is inferred */
  state?: 'ACTIVE' | 'BLOCKED' | 'OFFBOARDED' | 'DELETED';
  /** auth account type */
  type?: string;
  early_adopter?: boolean;
  email?: string;
  /** personal identity first + last name; absent when there's no identity record */
  name?: string;
  pro_terms_accepted?: string;
  /** lists RESTRICTED features — the UI booleans are the inverse */
  feature_restrictions?: string[];
  is_blocked?: boolean;
  is_pending?: boolean;
  is_staff?: boolean;
  pending_items?: string[];
  /** an open proof of funds review, which holds withdrawals only */
  proof_of_funds?: ProofOfFundsStatus;
  account_holder_name?: string | null;
  aml_eur_threshold?: number;
  citizenship_country_code?: string | null;
  residence_country_code?: string | null;
  address?: string | null;
  enabled_fiat_withdrawals?: boolean;
  enabled_fiat_deposits?: boolean;
  enabled_crypto_deposits?: boolean;
  enabled_crypto_withdrawals?: boolean;
  verification_date?: string;
  verification_status?: string;
  verification_review_status?: string;
  verification_review_answer?: string;
  verification_review_reject_type?: string;
  verification_review_reject_labels?: string[];
  status_change_action?: string;
  status_change_at?: number | string;
  /** broker clients only, and only when bop-api resolved the broker — sent together */
  broker_id?: string;
  broker_name?: string;
  /** precedence-resolved server-side (dynamo ?? funds ?? RETAIL) — never re-derive client-side */
  segment: UserSegment;
}

/** Reshaped user record the whole console renders from (legacy casing kept). */
export interface ConsoleUser {
  PID: string;
  FastId: string | null;
  created: string;
  /** raw epoch kept alongside the formatted string so components can re-format */
  createdAt?: number;
  earlyAdopter?: boolean;
  email?: string;
  name?: string;
  spotEnabled: boolean;
  futuresEnabled: boolean;
  instantTradeEnabled: boolean;
  proTermsAccepted?: string;
  segment: UserSegment;
  staff: boolean;
  state: UserState;
  statusChangeAction?: StatusChangeAction;
  statusChangeAt?: string;
  pendingItems?: string[];
  /** an open proof of funds review, which holds withdrawals until Compliance clear it */
  proofOfFunds?: ProofOfFundsStatus;
  type?: string;
  verificationDate?: string;
  verificationStatus?: string;
  userType: UserType;
  /** the broker a broker client belongs to; `id` keys /operations/brokers/:brokerId */
  broker?: { id: string; name: string };
}

/** Shared contract: every user-console tab component takes exactly these props. */
export interface UserTabProps {
  /** canonical PID (post-normalization) */
  pid: string;
  user: ConsoleUser;
}

const ALLOWED_STATUS_CHANGE_ACTIONS: ReadonlySet<string> = new Set(['ACTIVE', 'BLOCKED', 'PENDING', 'OFFBOARDED', 'CLOSED', 'REACTIVATED']);

export const RESTRICTED_STATES: readonly UserState[] = ['BLOCKED', 'OFFBOARDED', 'DELETED'];

const BROKER_CLIENT_TYPES: readonly UserType[] = ['BROKER_CLIENT', 'BROKER_CLIENT_SUBACCOUNT'];

/** Broker clients log in through their broker — they have no One Trading (Auth0) login. */
export const isBrokerClientType = (userType: UserType): boolean => BROKER_CLIENT_TYPES.includes(userType);

export const isRestrictedState = (state: string | null | undefined): boolean => RESTRICTED_STATES.includes(state as UserState);

/** '' for falsy, CLOSED for DELETED (the UI never shows the word "DELETED"), else verbatim. */
export const formatUserStateLabel = (state: string | null | undefined): string => {
  if (!state) return '';
  return state === 'DELETED' ? 'CLOSED' : String(state);
};

export const inferUserState = (raw: RawUser): UserState => {
  // Precedence mirrors bop-api deriveUserStatus; the more restrictive state always wins.
  if (raw.state === 'OFFBOARDED') return 'OFFBOARDED';
  if (raw.state === 'DELETED') return 'DELETED';
  if (raw.state === 'BLOCKED' || raw.is_blocked) return 'BLOCKED';
  if (raw.is_pending) return 'PENDING';
  return raw.state ?? 'ACTIVE';
};

/** Pure RawUser → ConsoleUser reshape moved client-side from the Nuxt BFF. */
export const mapRawUser = (raw: RawUser): ConsoleUser => {
  // FIX: a missing `created` used to parseInt to NaN and render 'NaN' — guard to 'not set'
  const createdEpoch = raw.created ? Number.parseInt(raw.created, 10) : Number.NaN;
  const hasCreated = Number.isFinite(createdEpoch);

  const statusChangeAction =
    typeof raw.status_change_action === 'string' && ALLOWED_STATUS_CHANGE_ACTIONS.has(raw.status_change_action) ? (raw.status_change_action as StatusChangeAction) : undefined;
  const statusChangeAtEpoch = parseEpochNumber(raw.status_change_at);
  // Formatted only when BOTH a whitelisted action AND a parsed epoch exist
  const statusChangeAt = statusChangeAction && statusChangeAtEpoch !== undefined ? formatFriendlyTimestamp(statusChangeAtEpoch) : undefined;

  return {
    PID: raw.pid,
    FastId: raw.fast_id,
    created: hasCreated ? formatFriendlyTimestamp(createdEpoch) : 'not set',
    createdAt: hasCreated ? createdEpoch : undefined,
    earlyAdopter: raw.early_adopter,
    email: raw.email,
    name: raw.name?.trim() || undefined,
    // The wire array lists restricted features; note INSTANT-TRADE uses a hyphen
    spotEnabled: !raw.feature_restrictions?.includes('SPOT'),
    futuresEnabled: !raw.feature_restrictions?.includes('FUTURES'),
    instantTradeEnabled: !raw.feature_restrictions?.includes('INSTANT-TRADE'),
    proTermsAccepted: raw.pro_terms_accepted ? formatDate(Number.parseInt(raw.pro_terms_accepted, 10)) : undefined,
    segment: raw.segment,
    staff: raw.is_staff ?? false,
    state: inferUserState(raw),
    statusChangeAction,
    statusChangeAt,
    pendingItems: raw.pending_items,
    proofOfFunds: raw.proof_of_funds,
    type: raw.type,
    verificationDate: raw.verification_date ? formatDate(raw.verification_date) : undefined,
    verificationStatus: raw.verification_status,
    userType: raw.user_type,
    broker: raw.broker_id && raw.broker_name ? { id: raw.broker_id, name: raw.broker_name } : undefined,
  };
};

/** `id` may be a PID (uuid), a Fast ID, or an email — bop-api resolves it; the returned PID is authoritative. */
export const getConsoleUser = async (id: string): Promise<ConsoleUser> => {
  const raw = await bopApi.get(`v1/user/${id}`).json<RawUser>();
  return mapRawUser(raw);
};

// FundsCustomer + getFundsCustomer (GET /v1/user/funds/customer/{pid})
// live in src/services/api/user-access.ts — the consumer-adjacent module that also
// owns the update path. The copy that used to live here was dead code.

export const USER_SEGMENTS = ['RETAIL', 'MARKET_MAKER', 'INSTITUTIONAL'] as const;

const INSTITUTIONAL_SEGMENTS: readonly UserSegment[] = ['INSTITUTIONAL', 'MARKET_MAKER'];

export const isInstitutionalSegment = (segment: UserSegment): boolean => INSTITUTIONAL_SEGMENTS.includes(segment);

/** INSTITUTIONAL↔MARKET_MAKER does NOT cross; only retail↔institutional moves do. */
export const crossesRetailBoundary = (from: UserSegment, to: UserSegment): boolean => isInstitutionalSegment(from) !== isInstitutionalSegment(to);

export interface UpdateSegmentRequest {
  /** 35–500 chars — bop-api zod-enforces (INVALID_COMMENT) */
  comment: string;
  segment: UserSegment;
  /**
   * REQUIRED whenever the change crosses the retail/institutional boundary (else 400
   * FEATURE_RESTRICTIONS_DECISION_REQUIRED); MUST be omitted otherwise.
   */
  applySegmentFeatureRestrictions?: boolean;
}

/** 200 body of POST /v1/user/{pid}/segment (full success or no-op). */
export interface ChangeSegmentResult {
  error: null;
  from: { dynamo: string | null; funds: string; isMarketMaker: boolean } | null;
  to: string;
  applied: string[];
  failed: null;
  skipped: string[];
  statusChange: 'ACTIVE' | 'PENDING' | null;
  noop?: true;
}

export const updateSegment = (pid: string, body: UpdateSegmentRequest): Promise<ChangeSegmentResult> =>
  bopApi.post(`v1/user/${pid}/segment`, { json: body }).json<ChangeSegmentResult>();

export interface SegmentPartialFailure {
  applied: string[];
  failed: { target: string; error: string };
}

/**
 * 500 PARTIAL_SEGMENT_UPDATE detection: some stores updated, some not, nothing
 * rolled back. ApiError promotes body.error to error.message, so match on that and re-read
 * applied/failed from the (cloned) response body. Retry is safe — writes are blind-writes.
 */
export const parseSegmentPartialFailure = async (error: unknown): Promise<SegmentPartialFailure | null> => {
  if (!(error instanceof HTTPError) || error.message !== 'PARTIAL_SEGMENT_UPDATE') return null;
  try {
    const body = (await error.response.clone().json()) as { applied?: string[]; failed?: { target?: string; error?: string } };
    return {
      applied: body.applied ?? [],
      failed: { target: body.failed?.target ?? 'unknown', error: body.failed?.error ?? 'unknown' },
    };
  } catch {
    return null;
  }
};

/** Known segment error codes mapped to friendly copy. */
export const SEGMENT_ERROR_COPY: Record<string, string> = {
  FUNDS_CUSTOMER_NOT_FOUND: 'This user has no funds customer record yet.',
  USER_NOT_ACTIVE: 'Unavailable while the user is not active.',
};
